import { SortOrders } from 'types/filters';
import { ModuleSortConfig, SortOption } from 'types/sorting';

import { SortField } from 'constants/enums';

/**
 * Legacy sort fields (old backend).
 * Kept separate so they are easy to remove after migration.
 */
export const LEGACY_SORT_FIELDS = {
  CreatedDate: 'createdDate',
  EditDate: 'editDate',
  Name: 'name',
  Status: 'status',
  StartDate: 'startDate',
  SeparationDate: 'separationDate',
  JobseekerFirstName: 'jobseeker.firstName',
  LastAceCheckInDate: 'lastAceCheckInDate'
} as const;

const createSortOption = (field: string, order?: SortOrders): SortOption => ({
  field,
  ...(order ? { order } : {})
});

export const getModuleSortConfig = (
  t: (key: string) => string
): { [key: string]: ModuleSortConfig } => ({
  jobseekers: {
    options: {
      BEST_MATCH: createSortOption(SortField.Relevance),
      CREATED_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Asc
      ),
      CREATED_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Desc
      )
    },
    labels: {
      BEST_MATCH: t('sort.mostRelevant'),
      CREATED_DATE_ASC: t('sort.mostRecent'),
      CREATED_DATE_DESC: t('sort.leastRecent')
    },
    defaultOption: 'BEST_MATCH'
  },
  jobs: {
    options: {
      CREATED_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Asc
      ),
      CREATED_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Desc
      )
    },
    labels: {
      CREATED_DATE_ASC: t('sort.mostRecent'),
      CREATED_DATE_DESC: t('sort.leastRecent')
    },
    defaultOption: 'CREATED_DATE_ASC'
  },
  clients: {
    options: {
      CREATED_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Asc
      ),
      CREATED_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Desc
      ),
      EDIT_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.EditDate,
        SortOrders.Asc
      ),
      EDIT_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.EditDate,
        SortOrders.Desc
      ),
      NAME_ASC: createSortOption(LEGACY_SORT_FIELDS.Name, SortOrders.Asc),
      NAME_DESC: createSortOption(LEGACY_SORT_FIELDS.Name, SortOrders.Desc),
      STATUS_ACTIVE: createSortOption(
        LEGACY_SORT_FIELDS.Status,
        SortOrders.Asc
      ),
      STATUS_SERVICE: createSortOption(
        LEGACY_SORT_FIELDS.Status,
        SortOrders.Desc
      )
    },
    labels: {
      CREATED_DATE_ASC: t('sort.createdDateAsc'),
      CREATED_DATE_DESC: t('sort.createdDateDesc'),
      EDIT_DATE_ASC: t('sort.editDateAsc'),
      EDIT_DATE_DESC: t('sort.editDateDesc'),
      NAME_ASC: t('sort.clientNameAZ'),
      NAME_DESC: t('sort.clientNameZA'),
      STATUS_ACTIVE: t('sort.statusActiveFirst'),
      STATUS_SERVICE: t('sort.statusServiceFirst')
    },
    defaultOption: 'NAME_ASC'
  },
  applications: {
    options: {
      START_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.StartDate,
        SortOrders.Asc
      ),
      START_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.StartDate,
        SortOrders.Desc
      ),
      CREATED_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Asc
      ),
      CREATED_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Desc
      ),
      EDIT_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.EditDate,
        SortOrders.Asc
      ),
      EDIT_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.EditDate,
        SortOrders.Desc
      )
    },
    labels: {
      START_DATE_ASC: t('sort.startDateAsc'),
      START_DATE_DESC: t('sort.startDateDesc'),
      CREATED_DATE_ASC: t('sort.createdDateAsc'),
      CREATED_DATE_DESC: t('sort.createdDateDesc'),
      EDIT_DATE_ASC: t('sort.editDateAsc'),
      EDIT_DATE_DESC: t('sort.editDateDesc')
    },
    defaultOption: 'CREATED_DATE_DESC'
  },
  ace: {
    options: {
      FIRST_NAME_ASC: createSortOption(
        LEGACY_SORT_FIELDS.JobseekerFirstName,
        SortOrders.Asc
      ),
      FIRST_NAME_DESC: createSortOption(
        LEGACY_SORT_FIELDS.JobseekerFirstName,
        SortOrders.Desc
      ),
      LAST_ACE_CONTACT_ASC: createSortOption(
        LEGACY_SORT_FIELDS.LastAceCheckInDate,
        SortOrders.Asc
      ),
      LAST_ACE_CONTACT_DESC: createSortOption(
        LEGACY_SORT_FIELDS.LastAceCheckInDate,
        SortOrders.Desc
      ),
      START_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.StartDate,
        SortOrders.Asc
      ),
      START_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.StartDate,
        SortOrders.Desc
      ),
      SEPARATION_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.SeparationDate,
        SortOrders.Asc
      ),
      SEPARATION_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.SeparationDate,
        SortOrders.Desc
      )
    },
    labels: {
      FIRST_NAME_ASC: t('sort.nameAZ'),
      FIRST_NAME_DESC: t('sort.nameZA'),
      LAST_ACE_CONTACT_ASC: t('sort.lastAceContactAsc'),
      LAST_ACE_CONTACT_DESC: t('sort.lastAceContactDesc'),
      START_DATE_ASC: t('sort.startDateAsc'),
      START_DATE_DESC: t('sort.startDateDesc'),
      SEPARATION_DATE_ASC: t('sort.separationDateAsc'),
      SEPARATION_DATE_DESC: t('sort.separationDateDesc')
    },
    defaultOption: 'START_DATE_DESC'
  },
  recruitmentOverviewReport: {
    options: {
      CREATED_DATE_ASC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Asc
      ),
      CREATED_DATE_DESC: createSortOption(
        LEGACY_SORT_FIELDS.CreatedDate,
        SortOrders.Desc
      )
    },
    labels: {
      CREATED_DATE_ASC: t('sort.createdDateAsc'),
      CREATED_DATE_DESC: t('sort.createdDateDesc')
    },
    defaultOption: 'CREATED_DATE_ASC'
  },
  default: {
    options: {},
    labels: {},
    defaultOption: ''
  }
});
